import React, { useEffect, useRef } from 'react';
import {
  $createNodeSelection,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isLineBreakNode,
  $isNodeSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  BEFORE_INPUT_COMMAND,
  COMMAND_PRIORITY_HIGH,
  DELETE_CHARACTER_COMMAND,
  INSERT_LINE_BREAK_COMMAND,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ENTER_COMMAND,
  PASTE_COMMAND,
  RootNode,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { MarkResult } from '$/components/InlineBox/types';
import {
  getEditorTextLength,
  getTextNodeCaretStartOffset,
  insertTextAtSelection,
  normalizeMentionTextAnchors,
  serializeEditorStateToContent,
  syncMarkInlineBoxOrder,
} from './editor';
import { $isInlineBoxNode, $isMentionNode } from './nodes';
import {
  isPostTaskInlineBoxData,
  POST_TASK_CARET_ANCHOR,
  serializePostTaskLeadingCaretText,
} from './postTaskLabel';
import { EditorSerializedContent } from './types';

/**
 * 在 transform/update 的 $ 上下文中直接计算可见文本长度。
 * 与 getEditorTextLength 逻辑一致（去 ZWS、Mention 计 @name），但不经过
 * editorState.read()，避免 transform 循环中读到未更新的快照。
 */
function $getVisibleTextLength(): number {
  const root = $getRoot();
  let length = 0;
  let shouldIgnorePostTaskCaretAnchor = false;
  const children = root.getChildren();
  children.forEach((child, index) => {
    const traverse = (node: any) => {
      if ($isMentionNode(node)) {
        shouldIgnorePostTaskCaretAnchor = false;
        const mention = node.getMention();
        length += mention.name.length + 1; // "@" + name
        return;
      }
      if ($isTextNode(node)) {
        const nextSibling = node.getNextSibling();
        const isPostTaskLeadingCaretText = (
          $isInlineBoxNode(nextSibling)
          && isPostTaskInlineBoxData(nextSibling.__inlineBox.data)
        );
        const text = isPostTaskLeadingCaretText
          ? serializePostTaskLeadingCaretText(node.getTextContent())
          : node.getTextContent().replace(/\u200B/g, '');
        length += shouldIgnorePostTaskCaretAnchor && text.startsWith(POST_TASK_CARET_ANCHOR)
          ? text.slice(POST_TASK_CARET_ANCHOR.length).length
          : text.length;
        shouldIgnorePostTaskCaretAnchor = false;
        return;
      }
      if ($isLineBreakNode(node)) {
        shouldIgnorePostTaskCaretAnchor = false;
        length += 1;
        return;
      }
      if ($isInlineBoxNode(node)) {
        shouldIgnorePostTaskCaretAnchor = isPostTaskInlineBoxData(node.__inlineBox.data);
        return;
      }
      if ($isElementNode(node)) {
        node.getChildren().forEach(traverse);
      }
    };
    traverse(child);
    if ($isParagraphNode(child) && index < children.length - 1) {
      length += 1;
    }
  });
  return length;
}

export function EditablePlugin({ disabled }: { disabled: boolean; }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  return null;
}

export function MarkOrderTransformPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerNodeTransform(RootNode, () => {
      syncMarkInlineBoxOrder();
      normalizeMentionTextAnchors();
    });
  }, [editor]);

  return null;
}

export function MaxLengthPlugin({ maxLength }: { maxLength?: number; }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!maxLength) return () => {};

    const removeCommand = editor.registerCommand(
      BEFORE_INPUT_COMMAND,
      (event) => {
        const inputEvent = event as InputEvent | null;
        if (!inputEvent) return false;

        // 删除操作和 IME 组合输入期间的事件不拦截：
        // 组合期间删除拼音字母时 inputType 为 insertCompositionText（非 delete 开头），
        // 若阻止会导致 IME 无法删除最后一个字母。
        const { inputType } = inputEvent;
        if (inputType?.startsWith('delete') || inputType?.includes('omposition')) return false;

        const currentLength = getEditorTextLength(editor.getEditorState());
        if (currentLength >= maxLength) return true;

        const text = inputEvent.data || '';
        if (!text) return false;

        if (currentLength + text.length > maxLength) {
          const allowed = text.slice(0, maxLength - currentLength);
          if (allowed) {
            insertTextAtSelection(allowed);
          }
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    // Shift+Enter 产生的 INSERT_LINE_BREAK_COMMAND 不经过 BEFORE_INPUT_COMMAND，
    // 需要单独拦截，避免在已达字数上限时仍能插入换行。
    const removeLineBreakCommand = editor.registerCommand(
      INSERT_LINE_BREAK_COMMAND,
      () => {
        const currentLength = $getVisibleTextLength();
        return currentLength >= maxLength;
      },
      COMMAND_PRIORITY_HIGH,
    );

    // 后置兜底：IME 确认、语音输入、浏览器自动填充等绕过 BEFORE_INPUT_COMMAND 的场景，
    // 在 RootNode transform 中检测并截断超限文本。
    const removeTransform = editor.registerNodeTransform(RootNode, () => {
      // 直接从当前活跃根节点计算可见长度，避免 editor.getEditorState() 在
      // transform 循环中返回尚未更新的快照导致重复裁剪清空内容。
      const currentLength = $getVisibleTextLength();
      if (currentLength <= maxLength) return;

      const overflow = currentLength - maxLength;
      // 收集所有叶子节点（TextNode + LineBreakNode），从末尾开始裁剪，
      // 确保 Shift+Enter 产生的 LineBreakNode 也能被正确移除，
      // 避免仅裁剪 TextNode 导致可见文字被错误截断。
      const root = $getRoot();
      const allLeaves: any[] = [];
      const collectLeaves = (node: any) => {
        if ($isTextNode(node) || $isLineBreakNode(node)) {
          allLeaves.push(node);
        } else if ($isElementNode(node)) {
          node.getChildren().forEach(collectLeaves);
        }
      };
      root.getChildren().forEach(collectLeaves);

      let remaining = overflow;
      let lastTrimmedTextNode: any = null;
      for (let i = allLeaves.length - 1; i >= 0 && remaining > 0; i -= 1) {
        const node = allLeaves[i];
        if ($isLineBreakNode(node)) {
          node.remove();
          remaining -= 1;
        } else if ($isTextNode(node)) {
          const text = node.getTextContent();
          const nextSibling = node.getNextSibling();
          const isPostTaskLeadingCaretText = (
            $isInlineBoxNode(nextSibling)
            && isPostTaskInlineBoxData(nextSibling.__inlineBox.data)
          );
          if (isPostTaskLeadingCaretText) {
            const visibleText = serializePostTaskLeadingCaretText(text);
            const visibleLen = visibleText.length;
            if (visibleLen <= remaining) {
              remaining -= visibleLen;
              node.setTextContent(POST_TASK_CARET_ANCHOR);
            } else {
              node.setTextContent(
                `${visibleText.slice(0, visibleLen - remaining)}${POST_TASK_CARET_ANCHOR}`,
              );
              lastTrimmedTextNode = node;
              remaining = 0;
            }
          } else {
            // 跳过纯零宽锚点节点，保留 InlineBox/Mention 旁的光标承接
            const previousSibling = node.getPreviousSibling();
            const isPostTaskCaretText = (
              $isInlineBoxNode(previousSibling)
              && isPostTaskInlineBoxData(previousSibling.__inlineBox.data)
            );
            const visibleStart = isPostTaskCaretText
              ? text.search(new RegExp(`[^\\u200B${POST_TASK_CARET_ANCHOR}]`))
              : text.search(/[^\u200B]/);
            if (visibleStart >= 0) {
              const visibleLen = text.length - visibleStart;
              if (visibleLen <= remaining) {
                remaining -= visibleLen;
                // 保留 ZWS 前缀（光标锚点）
                node.setTextContent(text.slice(0, visibleStart) || '');
              } else {
                node.setTextContent(text.slice(0, text.length - remaining));
                lastTrimmedTextNode = node;
                remaining = 0;
              }
            }
          }
        }
      }

      // 裁剪后显式将光标定位到裁剪位置末尾，避免 IME 结束后浏览器选区与实际
      // 文本长度不一致导致后续 backspace 需要多次按键才能删除可见内容。
      if (lastTrimmedTextNode) {
        const endOffset = lastTrimmedTextNode.getTextContentSize();
        lastTrimmedTextNode.select(endOffset, endOffset);
      }

      // 裁剪可能把 InlineBox/Mention 旁的零宽锚点文本节点内容清空或截断，
      // 必须修复锚点，否则方向键导航会卡在空文本节点上无法移动光标。
      normalizeMentionTextAnchors();
    });

    return () => {
      removeCommand();
      removeLineBreakCommand();
      removeTransform();
    };
  }, [editor, maxLength]);

  return null;
}

/**
 * 从剪贴板 DataTransfer 读取可用的纯文本：优先 text/plain，其次从 text/html 解析纯文本，最后尝试 text/uri-list。
 * 避免仅有 HTML/链接时误判为空，以及误拦截图片等非纯文本粘贴（由其它处理器接管）。
 */
function readPlainTextFromClipboardData(data: DataTransfer | null | undefined): string {
  if (!data) return '';
  const plain = data.getData('text/plain') || '';
  if (plain.trim()) return plain;
  const html = data.getData('text/html') || '';
  if (html.trim()) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const extracted = doc.body?.textContent?.trim() || '';
      if (extracted) return extracted;
    } catch {
      // 解析失败则继续尝试其它 MIME
    }
  }
  const uriList = data.getData('text/uri-list') || '';
  return uriList.trim() ? uriList.split('\n')[0].trim() : '';
}

export function PastePlainTextPlugin({ maxLength }: { maxLength?: number; }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        if (!event) return false;
        const clipboardEvent = event as ClipboardEvent;
        const rawText = readPlainTextFromClipboardData(clipboardEvent.clipboardData);
        if (!rawText) return false;

        const text = rawText;

        clipboardEvent.preventDefault();
        clipboardEvent.stopPropagation();

        editor.update(() => {
          let insertText = text;
          if (maxLength) {
            const length = getEditorTextLength(editor.getEditorState());
            const remain = maxLength - length;
            if (remain <= 0) return;
            if (insertText.length > remain) insertText = insertText.slice(0, remain);
          }

          if (!insertTextAtSelection(insertText)) {
            const root = $getRoot();
            const paragraph = $createParagraphNode();
            root.append(paragraph);
            paragraph.selectEnd();
            insertTextAtSelection(insertText);
          }
        });
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, maxLength]);

  return null;
}

export function EnterSubmitPlugin({
  onEnter,
  isComposingRef,
  onBeforeEnter,
  markResultsMapRef,
}: {
  onEnter?: (value: EditorSerializedContent) => void;
  isComposingRef: React.MutableRefObject<boolean>;
  onBeforeEnter?: () => boolean;
  markResultsMapRef?: React.MutableRefObject<Map<string, unknown[]>>;
}) {
  const [editor] = useLexicalComposerContext();
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;
  const onBeforeEnterRef = useRef(onBeforeEnter);
  onBeforeEnterRef.current = onBeforeEnter;

  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (isComposingRef.current) return false;
        // Shift+Enter 走默认换行，不触发提交（与常见聊天输入一致）
        if (event?.shiftKey) return false;
        if (onBeforeEnterRef.current?.() || event?.defaultPrevented) {
          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }
          return true;
        }
        // 未配置 onEnter 时走默认换行，不拦截回车。
        if (!onEnterRef.current) {
          return false;
        }
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        const content = serializeEditorStateToContent(
          editor.getEditorState(),
          markResultsMapRef?.current as Map<string, MarkResult[]> | undefined,
        );
        onEnterRef.current(content);
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, isComposingRef, markResultsMapRef]);

  return null;
}

/** Backspace/Delete 紧邻行内装饰节点（InlineBox / Mention）时，一次按键直接删除该节点。 */
export function DecoratorBackspacePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    /** 判断节点是否为行内装饰（InlineBox 或 Mention） */
    const isInlineDecorator = (node: any) => $isInlineBoxNode(node) || $isMentionNode(node);

    /** 判断文本节点是否仅包含零宽锚点字符 */
    const isOnlyAnchorText = (node: any) => {
      if (!$isTextNode(node)) return false;
      const text = node.getTextContent();
      return text.length > 0 && text === '\u200B'.repeat(text.length);
    };

    /** 在待删除装饰节点原位置插入临时光标锚点，避免删除 ZWS 后选区按旧 index 掉到段落开头。 */
    const createRemovalCaretAnchor = (node: any) => {
      if (!isInlineDecorator(node)) return null;
      const caretAnchor = $createTextNode('\u200B');
      node.insertBefore(caretAnchor);
      return caretAnchor;
    };

    /** 将光标落到删除前预留的锚点内部，避免后方文本合并后父级 offset 被映射到段落末尾。 */
    const selectRemovalCaretAnchor = (caretAnchor: any) => {
      const parent = caretAnchor?.getParent?.();
      if (!$isTextNode(caretAnchor) || !$isElementNode(parent)) return false;
      caretAnchor.select(caretAnchor.getTextContentSize(), caretAnchor.getTextContentSize());
      return true;
    };

    /** 将光标放到目标节点之前，遇到行内装饰节点时优先落在其前方文本锚点末尾。 */
    const selectBeforeNode = (node: any) => {
      if ($isTextNode(node)) {
        const startOffset = getTextNodeCaretStartOffset(node);
        node.select(startOffset, startOffset);
        return true;
      }
      if (isInlineDecorator(node)) {
        const prev = node.getPreviousSibling();
        if ($isTextNode(prev)) {
          const len = prev.getTextContentSize();
          prev.select(len, len);
          return true;
        }
        const parent = node.getParent();
        if ($isElementNode(parent)) {
          const idx = node.getIndexWithinParent();
          parent.select(idx, idx);
          return true;
        }
      }
      return false;
    };

    /** 将光标放到目标节点之后，遇到行内装饰节点时优先落在其后方文本锚点起始位。 */
    const selectAfterNode = (node: any) => {
      if ($isTextNode(node)) {
        const len = node.getTextContentSize();
        node.select(len, len);
        return true;
      }
      if (isInlineDecorator(node)) {
        const next = node.getNextSibling();
        if ($isTextNode(next)) {
          const startOffset = getTextNodeCaretStartOffset(next);
          next.select(startOffset, startOffset);
          return true;
        }
        const parent = node.getParent();
        if ($isElementNode(parent)) {
          const idx = node.getIndexWithinParent();
          parent.select(idx + 1, idx + 1);
          return true;
        }
      }
      return false;
    };

    /** 删除 NodeSelection 后重建 RangeSelection，保证连续按 Delete/Backspace 仍能命中相邻内容。 */
    const selectAfterDecoratorRemoval = (
      parent: any,
      index: number,
      direction: 'backward' | 'forward',
    ) => {
      if (!$isElementNode(parent)) return;
      const children = parent.getChildren();
      const right = children[Math.min(index, children.length - 1)];
      const left = children[Math.min(index - 1, children.length - 1)];

      if (direction === 'forward') {
        if (right && selectBeforeNode(right)) return;
        if (left && selectAfterNode(left)) return;
      } else {
        if (left && selectAfterNode(left)) return;
        if (right && selectBeforeNode(right)) return;
      }

      const safeIndex = Math.max(0, Math.min(index, children.length));
      parent.select(safeIndex, safeIndex);
    };

    /**
     * NodeSelection 下删除装饰节点（点击/方向键选中后按 Backspace/Delete）。
     * 同时清理装饰节点两侧的孤立 ZWS 锚点文本节点。
     */
    const deleteNodeSelection = (event: KeyboardEvent | null, direction: 'backward' | 'forward'): boolean => {
      const selection = $getSelection();
      if (!$isNodeSelection(selection)) return false;
      const nodes = selection.getNodes();
      const decorators = nodes.filter((n) => isInlineDecorator(n));
      if (!decorators.length) return false;
      decorators.forEach((node) => {
        const parent = node.getParent();
        const index = node.getIndexWithinParent();
        const prev = node.getPreviousSibling();
        const next = node.getNextSibling();
        const caretAnchor = createRemovalCaretAnchor(node);
        node.remove();
        if (prev && isOnlyAnchorText(prev)) prev.remove();
        if (next && isOnlyAnchorText(next)) next.remove();
        normalizeMentionTextAnchors();
        if (!selectRemovalCaretAnchor(caretAnchor)) {
          selectAfterDecoratorRemoval(parent, index, direction);
        }
      });
      event?.preventDefault();
      return true;
    };

    /**
     * KEY_BACKSPACE / KEY_DELETE 处理：
     * 光标紧邻装饰节点（Mention / InlineBox）时拦截，防止 Lexical 默认 deleteCharacter
     * 通过 Selection.modify 跨越 contenteditable=false 边界错误删除装饰节点。
     *
     * 重要：命令处理器由 triggerCommandListeners 的 updateEditorSync 调用，已在 Lexical
     * 更新上下文中运行。直接操作编辑树即可，不要再包 editor.update()：因为此时
     * editor._updating=true，editor.update() 会将回调推入队列延迟执行，导致本次
     * 同步返回值错误（handled 永远为 false），破坏 event.preventDefault() 和命令传播拦截。
     */
    const unregisterBackspace = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event) => {
        // NodeSelection（点击/方向键选中装饰节点）→ 直接删除
        if (deleteNodeSelection(event, 'backward')) return true;

        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
        const { anchor } = selection;

        if (anchor.type === 'text') {
          const textNode = anchor.getNode();
          if (!$isTextNode(textNode)) return false;
          const caretStart = getTextNodeCaretStartOffset(textNode);

          if (anchor.offset > caretStart) {
            // 光标在可见文本区域，前方紧邻装饰节点时手动 splice 仅删 1 个字符，
            // 防止 deleteCharacter 内部 Selection.modify 跨越 contenteditable=false 边界。
            const prev = textNode.getPreviousSibling();
            if (prev && isInlineDecorator(prev)) {
              textNode.spliceText(anchor.offset - 1, 1, '');
              normalizeMentionTextAnchors();
              const newCaretStart = getTextNodeCaretStartOffset(textNode);
              const newOffset = Math.max(anchor.offset - 1, newCaretStart);
              textNode.select(newOffset, newOffset);
              event?.preventDefault();
              return true;
            }
            return false;
          }

          const prev = textNode.getPreviousSibling();
          if (prev && isInlineDecorator(prev)) {
            // 光标在零宽锚点起始位，一次 Backspace 直接删除装饰节点。
            // 同时清理装饰节点前方的孤立 ZWS 锚点文本节点。
            const beforeAnchor = prev.getPreviousSibling();
            const parent = prev.getParent();
            const index = prev.getIndexWithinParent();
            const caretAnchor = createRemovalCaretAnchor(prev);
            if (isOnlyAnchorText(textNode)) {
              textNode.remove();
            }
            prev.remove();
            if (beforeAnchor && isOnlyAnchorText(beforeAnchor)) {
              beforeAnchor.remove();
            }
            normalizeMentionTextAnchors();
            if (!selectRemovalCaretAnchor(caretAnchor)) {
              selectAfterDecoratorRemoval(parent, index, 'backward');
            }
            event?.preventDefault();
            return true;
          }
        } else if (anchor.type === 'element') {
          const parent = anchor.getNode();
          if (!$isElementNode(parent)) return false;
          const child = parent.getChildAtIndex(anchor.offset - 1);
          if (child && isInlineDecorator(child)) {
            const index = child.getIndexWithinParent();
            const caretAnchor = createRemovalCaretAnchor(child);
            child.remove();
            normalizeMentionTextAnchors();
            if (!selectRemovalCaretAnchor(caretAnchor)) {
              selectAfterDecoratorRemoval(parent, index, 'backward');
            }
            event?.preventDefault();
            return true;
          }
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterDelete = editor.registerCommand(
      KEY_DELETE_COMMAND,
      (event) => {
        // NodeSelection（点击/方向键选中装饰节点）→ 直接删除
        if (deleteNodeSelection(event, 'forward')) return true;

        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
        const { anchor } = selection;

        if (anchor.type === 'text') {
          const textNode = anchor.getNode();
          if (!$isTextNode(textNode)) return false;
          if (anchor.offset < textNode.getTextContentSize()) return false;
          const next = textNode.getNextSibling();

          if (next && isInlineDecorator(next)) {
            const parent = next.getParent();
            const index = next.getIndexWithinParent() - (isOnlyAnchorText(textNode) ? 1 : 0);
            const caretAnchor = createRemovalCaretAnchor(next);
            if (isOnlyAnchorText(textNode)) {
              textNode.remove();
            }
            next.remove();
            normalizeMentionTextAnchors();
            if (!selectRemovalCaretAnchor(caretAnchor)) {
              selectAfterDecoratorRemoval(parent, index, 'forward');
            }
            event?.preventDefault();
            return true;
          }
        } else if (anchor.type === 'element') {
          const parent = anchor.getNode();
          if (!$isElementNode(parent)) return false;
          const child = parent.getChildAtIndex(anchor.offset);
          if (child && isInlineDecorator(child)) {
            const index = child.getIndexWithinParent();
            const caretAnchor = createRemovalCaretAnchor(child);
            child.remove();
            normalizeMentionTextAnchors();
            if (!selectRemovalCaretAnchor(caretAnchor)) {
              selectAfterDecoratorRemoval(parent, index, 'forward');
            }
            event?.preventDefault();
            return true;
          }
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    /**
     * DELETE_CHARACTER_COMMAND 兜底：
     * beforeinput → deleteContentBackward 也会走此命令；拦截装饰节点旁的删除，
     * 防止 PlainTextPlugin 的 deleteCharacter 通过 Selection.modify 越界。
     */
    const unregisterDeleteChar = editor.registerCommand(
      DELETE_CHARACTER_COMMAND,
      (isBackward: boolean) => {
        if (!isBackward) return false;
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
        const { anchor: delAnchor } = selection;
        if (delAnchor.type !== 'text') return false;
        const delTextNode = delAnchor.getNode();
        if (!$isTextNode(delTextNode)) return false;
        const delCaretStart = getTextNodeCaretStartOffset(delTextNode);
        if (delAnchor.offset <= delCaretStart) return false;
        const delPrev = delTextNode.getPreviousSibling();
        if (!delPrev || !isInlineDecorator(delPrev)) return false;
        // 前方紧邻装饰节点，手动 splice 防止 deleteCharacter 越界
        delTextNode.spliceText(delAnchor.offset - 1, 1, '');
        normalizeMentionTextAnchors();
        const newStart = getTextNodeCaretStartOffset(delTextNode);
        const newOffset = Math.max(delAnchor.offset - 1, newStart);
        delTextNode.select(newOffset, newOffset);
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );

    return () => {
      unregisterBackspace();
      unregisterDelete();
      unregisterDeleteChar();
    };
  }, [editor]);

  /**
   * compositionend 后修正：
   * 1. 光标修正：部分浏览器/IME 在 backspace 结束 composition 后，光标落在 ZWS 锚点区域，
   *    导致后续 backspace 从错误位置删除。在 microtask 中修正到文字末尾。
   * 2. 拼音残留清理：部分浏览器/IME 在 backspace 删除组合最后一个字母时，不会真正删除该字母，
   *    而是将其作为正式文本"提交"后结束组合。检测此场景并主动删除已提交的拼音。
   */
  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return () => {};

    /** 追踪组合输入期间最后一次按下的键，用于判断 compositionend 是否由 Backspace 触发 */
    let lastKeyDuringComposition: string | null = null;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.isComposing || e.keyCode === 229) {
        lastKeyDuringComposition = e.key;
      } else {
        lastKeyDuringComposition = null;
      }
    };

    const onCompositionEnd = (e: CompositionEvent) => {
      const cancelledByBackspace = lastKeyDuringComposition === 'Backspace';
      const committedText = e.data || '';
      lastKeyDuringComposition = null;

      queueMicrotask(() => {
        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || !selection.isCollapsed()) return;
          const { anchor } = selection;
          if (anchor.type !== 'text') return;
          const textNode = anchor.getNode();
          if (!$isTextNode(textNode)) return;
          const caretStart = getTextNodeCaretStartOffset(textNode);
          const textLen = textNode.getTextContentSize();

          // 光标在 ZWS 锚点区域但同一节点后面还有可见文字 → 移到末尾
          // 仅当 caretStart > 0（存在 ZWS 锚点）时才触发，否则 offset=0 是合法位置
          // 注意：Backspace 结束组合时不能 early return，否则后续拼音清理逻辑不会执行
          if (caretStart > 0 && anchor.offset <= caretStart && textLen > caretStart) {
            if (!cancelledByBackspace) {
              textNode.select(textLen, textLen);
              return;
            }
          }

          // Backspace 结束 IME 组合：浏览器将残留拼音字母"提交"为正式文本，
          // 但用户意图是取消输入。检测纯 ASCII 拼音并删除。
          if (cancelledByBackspace) {
            const text = textNode.getTextContent();
            let startOffset = -1;
            let endOffset = -1;

            if (committedText && /^[a-zA-Z]+$/.test(committedText)) {
              // 浏览器提交了拼音字母
              startOffset = anchor.offset - committedText.length;
              endOffset = anchor.offset;

              // 光标可能位于已提交文本之前（offset=0 或 ZWS 区域），需从 caretStart 处正向搜索
              if (startOffset < caretStart) {
                const idx = text.indexOf(committedText, caretStart);
                if (idx >= 0) {
                  startOffset = idx;
                  endOffset = idx + committedText.length;
                }
              }
            } else if (!committedText) {
              // committedText 为空：组合被取消但未提交任何文本。
              // 输入框可能为空或只有拼音锚点，需检查光标前是否有拼音字母并将其删除。
              // 检查光标前后各 10 个字符的窗口，找最后一个拼音字母段落。
              const checkStart = Math.max(caretStart, anchor.offset - 10);
              const checkEnd = anchor.offset;
              const snippet = text.substring(checkStart, checkEnd);
              const match = snippet.match(/([a-zA-Z]+)$/);
              if (match) {
                const letterLen = match[1].length;
                startOffset = checkEnd - letterLen;
                endOffset = checkEnd;
              } else {
                // 光标可能位于文本开头（offset=0 或 ZWS 锚点边界），
                // 向后方查找紧邻的残留拼音字母
                const forwardStart = Math.max(caretStart, anchor.offset);
                const forwardSnippet = text.substring(forwardStart, Math.min(forwardStart + 10, textLen));
                const forwardMatch = forwardSnippet.match(/^([a-zA-Z]+)/);
                if (forwardMatch) {
                  startOffset = forwardStart;
                  endOffset = forwardStart + forwardMatch[1].length;
                }
              }
            }

            if (startOffset >= caretStart && endOffset <= textLen && startOffset >= 0
              && text.substring(startOffset, endOffset).match(/^[a-zA-Z]+$/)) {
              const deleteLen = endOffset - startOffset;
              textNode.spliceText(startOffset, deleteLen, '');
              const newOffset = Math.max(startOffset, getTextNodeCaretStartOffset(textNode));
              textNode.select(newOffset, newOffset);
            } else if (caretStart > 0 && anchor.offset <= caretStart) {
              // 未找到可删除的拼音，但光标仍停留在 ZWS 区域 → 修正到可见文本位置
              const currentTextLen = textNode.getTextContentSize();
              if (currentTextLen > caretStart) {
                textNode.select(currentTextLen, currentTextLen);
              }
            }
          }
        }, { tag: 'skip-history' });
      });
    };

    rootElement.addEventListener('keydown', onKeyDown, true);
    rootElement.addEventListener('compositionend', onCompositionEnd);
    return () => {
      rootElement.removeEventListener('keydown', onKeyDown, true);
      rootElement.removeEventListener('compositionend', onCompositionEnd);
    };
  }, [editor]);

  return null;
}

interface PostTaskCaretGuardOptions {
  blockTrailingStart?: boolean;
  forceFirstLineStart?: boolean;
}

/** 当前折叠选区越过 post_task 左边界时，将其移动到标签后的首个可编辑位置。 */
export function $moveCaretAfterLeadingPostTask(options: PostTaskCaretGuardOptions = {}) {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;

  const root = $getRoot();
  const firstBlock = root.getFirstChild();
  if (!$isElementNode(firstBlock)) return false;

  const blockChildren = firstBlock.getChildren();
  const postTaskIndex = blockChildren.findIndex((node) => (
    $isInlineBoxNode(node) && isPostTaskInlineBoxData(node.__inlineBox.data)
  ));
  if (postTaskIndex < 0) return false;
  const postTaskNode = blockChildren[postTaskIndex];

  const trailingText = postTaskNode.getNextSibling();
  const caretStart = getTextNodeCaretStartOffset(trailingText);
  const { anchor } = selection;
  const anchorNode = anchor.getNode();
  let anchorChild = anchorNode;
  while (anchorChild.getParent() && anchorChild.getParent() !== firstBlock) {
    anchorChild = anchorChild.getParent()!;
  }
  const anchorChildIndex = anchorChild.getParent() === firstBlock
    ? anchorChild.getIndexWithinParent()
    : -1;
  const isBeforeTag = anchor.type === 'element'
    && (
      (anchorNode === root && anchor.offset === 0)
      || (anchorNode === firstBlock && anchor.offset <= postTaskIndex + 1)
    );
  const isInsideLeadingText = anchor.type === 'text'
    && anchorChildIndex >= 0
    && anchorChildIndex < postTaskIndex;
  const isInsideTrailingAnchor = anchor.type === 'text'
    && anchorNode === trailingText
    && anchor.offset < caretStart;
  const isAtTrailingStart = options.blockTrailingStart
    && anchor.type === 'text'
    && anchorNode === trailingText
    && anchor.offset <= caretStart;
  const firstLineBoundary = anchorNode === firstBlock
    ? anchor.offset
    : anchorChildIndex;
  const isOnFirstLine = firstLineBoundary >= postTaskIndex + 1
    && !blockChildren.slice(postTaskIndex + 1, firstLineBoundary).some($isLineBreakNode);
  const shouldForceFirstLineStart = options.forceFirstLineStart && isOnFirstLine;

  if (!isBeforeTag
    && !isInsideLeadingText
    && !isInsideTrailingAnchor
    && !isAtTrailingStart
    && !shouldForceFirstLineStart) return false;

  if ($isTextNode(trailingText)) {
    trailingText.select(caretStart, caretStart);
  } else {
    firstBlock.select(postTaskIndex + 1, postTaskIndex + 1);
  }
  return true;
}

/**
 * post_task 固定在富文本首位时，把鼠标、Home 和方向键产生的段首光标
 * 统一纠正到标签后的专用锚点，避免继续在标签前输入内容。
 */
export function PostTaskSelectionGuardPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.update(() => {
      $moveCaretAfterLeadingPostTask();
    }, { tag: 'skip-history' });

    const unregisterSelectionGuard = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      $moveCaretAfterLeadingPostTask,
      COMMAND_PRIORITY_HIGH,
    );

    /** 原生 Home 不会派发 Lexical 方向键命令，首行单独拦截到 post_task 右侧。 */
    const handleHomeKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Home' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;

      let handled = false;
      editor.update(() => {
        handled = $moveCaretAfterLeadingPostTask({ forceFirstLineStart: true });
      }, { discrete: true, tag: 'skip-history' });
      if (handled) {
        event.preventDefault();
      }
    };

    const unregisterRootListener = editor.registerRootListener((rootElement, previousRootElement) => {
      previousRootElement?.removeEventListener('keydown', handleHomeKeyDown, true);
      rootElement?.addEventListener('keydown', handleHomeKeyDown, true);
    });

    return () => {
      unregisterSelectionGuard();
      unregisterRootListener();
      editor.getRootElement()?.removeEventListener('keydown', handleHomeKeyDown, true);
    };
  }, [editor]);

  return null;
}

/**
 * 左右方向键处理装饰节点（InlineBox / Mention）导航：
 * - 第一次按键：选中装饰节点（NodeSelection），显示选中态
 * - 第二次按键：跳过装饰节点到另一侧
 * 同时跳过装饰节点旁的零宽锚点，不处理 Shift 选区扩展。
 */
export function DecoratorHorizontalArrowPlugin({
  isComposingRef,
}: {
  isComposingRef: React.MutableRefObject<boolean>;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    /** 后链路标签按固定富文本处理，不进入普通附件的两段式方向键选中态。 */
    const isInlineDecorator = (node: any) => (
      ($isInlineBoxNode(node) && !isPostTaskInlineBoxData(node.__inlineBox.data))
      || $isMentionNode(node)
    );

    /** 跳过装饰节点，将光标放到其右侧锚点文本可见位（如有）或元素偏移之后 */
    const selectAfterDecorator = (decoratorNode: any) => {
      const afterDecorator = decoratorNode.getNextSibling();
      if ($isTextNode(afterDecorator)) {
        const startOffset = getTextNodeCaretStartOffset(afterDecorator);
        afterDecorator.select(startOffset, startOffset);
      } else {
        const parent = decoratorNode.getParent();
        if ($isElementNode(parent)) {
          const idx = decoratorNode.getIndexWithinParent();
          parent.select(idx + 1, idx + 1);
        }
      }
    };

    /** 跳过装饰节点，将光标放到其左侧锚点文本末尾（如有）或元素偏移之前 */
    const selectBeforeDecorator = (decoratorNode: any) => {
      const beforeDecorator = decoratorNode.getPreviousSibling();
      if ($isTextNode(beforeDecorator)) {
        const len = beforeDecorator.getTextContentSize();
        beforeDecorator.select(len, len);
      } else {
        const parent = decoratorNode.getParent();
        if ($isElementNode(parent)) {
          const idx = decoratorNode.getIndexWithinParent();
          parent.select(idx, idx);
        }
      }
    };

    /** 将选区设为装饰节点的 NodeSelection（选中态） */
    const selectDecoratorNode = (node: any) => {
      const nodeSelection = $createNodeSelection();
      nodeSelection.add(node.getKey());
      $setSelection(nodeSelection);
    };

    /**
     * 按右键：
     * - 已在 NodeSelection 上：跳过装饰节点到其右侧
     * - 光标在文本末尾或元素偏移处且下一个兄弟是装饰节点时：选中该装饰节点
     * 注意：命令处理器已在 updateEditorSync 上下文中运行，不要再包 editor.update()。
     *
     * Lexical 在 onKeyDown 入口已用 editor.isComposing() 守卫，composing 期间
     * 不会 dispatch KEY_ARROW_*_COMMAND，因此此处不再重复检查 isComposingRef。
     */
    const unregisterRight = editor.registerCommand(
      KEY_ARROW_RIGHT_COMMAND,
      (event) => {
        if (!event || event.shiftKey || event.metaKey || event.ctrlKey) return false;
        const selection = $getSelection();

        // 已在 NodeSelection（装饰节点被选中）→ 跳到其右侧
        if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes();
          const target = nodes.find((n: any) => isInlineDecorator(n));
          if (target) {
            selectAfterDecorator(target);
            event.preventDefault();
            return true;
          }
          return false;
        }

        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
        const { anchor } = selection;

        // 处理 element 锚点（光标在两个子节点之间）
        if (anchor.type === 'element') {
          const el = anchor.getNode();
          if (!$isElementNode(el)) return false;
          const { offset } = anchor;
          const rightChild = el.getChildAtIndex(offset);
          if (!rightChild) return false;

          // 光标右侧直接是装饰节点：选中它
          if (isInlineDecorator(rightChild)) {
            selectDecoratorNode(rightChild);
            event.preventDefault();
            return true;
          }

          // 光标右侧是纯零宽锚点文本且其后方是装饰节点：选中该装饰节点
          if ($isTextNode(rightChild) && /^\u200B+$/.test(rightChild.getTextContent())) {
            const nextOfAnchor = rightChild.getNextSibling();
            if (nextOfAnchor && isInlineDecorator(nextOfAnchor)) {
              selectDecoratorNode(nextOfAnchor);
              event.preventDefault();
              return true;
            }
          }
          return false;
        }

        if (anchor.type !== 'text') return false;
        const textNode = anchor.getNode();
        if (!$isTextNode(textNode)) return false;
        if (anchor.offset < textNode.getTextContentSize()) {
          // 纯零宽锚点节点（全部是 \u200B，无实际文本），直接跳过，避免连续 InlineBox 间多一次无效按键
          const caretStart = getTextNodeCaretStartOffset(textNode);
          if (caretStart < textNode.getTextContentSize()) return false;
        }
        const next = textNode.getNextSibling();
        if (!next || !isInlineDecorator(next)) return false;
        // 选中装饰节点
        selectDecoratorNode(next);
        event.preventDefault();
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );

    /**
     * 按左键：
     * - 已在 NodeSelection 上：跳到装饰节点左侧
     * - 光标在锚点起始位且前一个兄弟是装饰节点时：选中该装饰节点
     * 注意：命令处理器已在 updateEditorSync 上下文中运行，不要再包 editor.update()。
     */
    const unregisterLeft = editor.registerCommand(
      KEY_ARROW_LEFT_COMMAND,
      (event) => {
        if (!event) return false;
        const selection = $getSelection();

        if ($isNodeSelection(selection)) {
          const postTaskNode = selection.getNodes().find((node) => (
            $isInlineBoxNode(node) && isPostTaskInlineBoxData(node.__inlineBox.data)
          ));
          if (postTaskNode) {
            selectAfterDecorator(postTaskNode);
            event.preventDefault();
            return true;
          }
        }

        if (!event.shiftKey && event.metaKey && !event.altKey && !event.ctrlKey
          && $moveCaretAfterLeadingPostTask({ forceFirstLineStart: true })) {
          event.preventDefault();
          return true;
        }

        if (!event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey
          && $moveCaretAfterLeadingPostTask({ blockTrailingStart: true })) {
          event.preventDefault();
          return true;
        }

        if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return false;

        // 已在 NodeSelection（装饰节点被选中）→ 跳到其左侧
        if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes();
          const target = nodes.find((n: any) => isInlineDecorator(n));
          if (target) {
            selectBeforeDecorator(target);
            event.preventDefault();
            return true;
          }
          return false;
        }

        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
        const { anchor } = selection;

        // 浏览器有时会将零宽锚点文本间的选区规范化为元素偏移，补齐此场景下的左跳逻辑
        if (anchor.type === 'element') {
          const el = anchor.getNode();
          if (!$isElementNode(el)) return false;
          const { offset } = anchor;
          if (offset <= 0) return false;
          const leftChild = el.getChildAtIndex(offset - 1);
          if (!leftChild) return false;

          // 光标左侧直接是装饰节点：选中它
          if (isInlineDecorator(leftChild)) {
            selectDecoratorNode(leftChild);
            event.preventDefault();
            return true;
          }

          // 光标左侧是纯零宽锚点文本且其前方是装饰节点：选中该装饰节点
          if ($isTextNode(leftChild) && /^\u200B+$/.test(leftChild.getTextContent())) {
            const prevOfAnchor = leftChild.getPreviousSibling();
            if (prevOfAnchor && isInlineDecorator(prevOfAnchor)) {
              selectDecoratorNode(prevOfAnchor);
              event.preventDefault();
              return true;
            }
          }
          return false;
        }

        if (anchor.type !== 'text') return false;
        const textNode = anchor.getNode();
        if (!$isTextNode(textNode)) return false;
        const caretStart = getTextNodeCaretStartOffset(textNode);
        if (anchor.offset > caretStart) return false;
        const prev = textNode.getPreviousSibling();
        if (prev && isInlineDecorator(prev)) {
          // 选中装饰节点
          selectDecoratorNode(prev);
          event.preventDefault();
          return true;
        }
        // 光标在 ZWS 锚点起始位，前方是 LineBreakNode：跳过 ZWS 到 LineBreak 之前
        if (prev && $isLineBreakNode(prev)) {
          const beforeLB = prev.getPreviousSibling();
          if (beforeLB && $isTextNode(beforeLB)) {
            const len = beforeLB.getTextContentSize();
            beforeLB.select(len, len);
          } else if (beforeLB && isInlineDecorator(beforeLB)) {
            selectDecoratorNode(beforeLB);
          } else {
            const parent = textNode.getParent();
            if ($isElementNode(parent)) {
              const idx = prev.getIndexWithinParent();
              parent.select(idx, idx);
            }
          }
          event.preventDefault();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    return () => {
      unregisterRight();
      unregisterLeft();
    };
  }, [editor, isComposingRef]);

  return null;
}
